import React, { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { round2, sumInstallments } from "@/lib/installments";
import { useCurrency } from "@/hooks/useUserCurrency";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Edit2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { InterestCalculator } from "./InterestCalculator";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useSubscriptionLimits } from "@/hooks/useSubscriptionLimits";
import { ProUpgradeModal } from "./ProUpgradeModal";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { calculateClientScore } from "@/lib/clientScore";
import { differenceInCalendarDays, startOfDay } from "date-fns";

const loanSchema = z.object({
  clientId: z.string().min(1, "Selecione um cliente"),
  amount: z.string()
    .min(1, "Valor é obrigatório")
    .refine((val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0, {
      message: "Valor deve ser maior que zero"
    })
    .refine((val) => parseFloat(val) <= 1000000, {
      message: "Valor deve ser no máximo R$ 1.000.000"
    }),
  interestRate: z.string().optional(),
  interestType: z.enum(["monthly", "fixed", "daily"], {
    errorMap: () => ({ message: "Selecione o tipo de juros" })
  }),
  installments: z.string().optional(),
  dailyDays: z.string().optional(),
  dailyPenalty: z.string().optional(),
  installmentFrequency: z.enum(["weekly", "biweekly", "monthly"]).optional(),
  loanDate: z.string().min(1, "Data do empréstimo é obrigatória"),
  dueDate: z.string().min(1, "Data de vencimento é obrigatória"),
}).refine((data) => {
  const loan = new Date(data.loanDate);
  const due = new Date(data.dueDate);
  return due > loan;
}, {
  message: "Data de vencimento deve ser posterior à data do empréstimo",
  path: ["dueDate"],
}).refine((data) => {
  if (data.interestType === "monthly" || data.interestType === "daily") {
    if (!data.interestRate || data.interestRate.trim() === "") {
      return false;
    }
    const rate = parseFloat(data.interestRate);
    return !isNaN(rate) && rate >= 0.1 && rate <= 100;
  }
  return true;
}, {
  message: "Taxa de juros é obrigatória e deve estar entre 0.1% e 100%",
  path: ["interestRate"],
}).refine((data) => {
  if (data.interestType === "fixed" && data.installments) {
    const installments = parseInt(data.installments);
    return !isNaN(installments) && installments >= 1;
  }
  return true;
}, {
  message: "Número de parcelas deve ser pelo menos 1",
  path: ["installments"],
}).refine((data) => {
  if (data.interestType === "daily") {
    const days = parseInt(data.dailyDays || "");
    return !isNaN(days) && days >= 1;
  }
  return true;
}, {
  message: "Informe a quantidade de dias para pagamento (mínimo 1)",
  path: ["dailyDays"],
}).refine((data) => {
  if (data.interestType === "daily" || data.interestType === "monthly") {
    const v = parseFloat(data.dailyPenalty || "");
    return !isNaN(v) && v >= 0;
  }
  return true;
}, {
  message: "Informe o valor da multa diária (R$)",
  path: ["dailyPenalty"],
});

const getLocalDateInputValue = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

interface AddLoanDialogProps {
  onSuccess: () => void;
  trigger?: React.ReactNode;
}

export function AddLoanDialog({ onSuccess, trigger }: AddLoanDialogProps) {
  const [open, setOpen] = useState(false);
  const { format: fmtMoney, country: fmtCountry } = useCurrency();
  const [showProModal, setShowProModal] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  const [renamingClient, setRenamingClient] = useState(false);
  const queryClient = useQueryClient();
  const { maxLoans, isPro } = useSubscriptionLimits();

  // Query to count current loans
  const { data: loansCount = 0 } = useQuery({
    queryKey: ["loans-count"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const { count, error } = await supabase
        .from("loans")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      if (error) return 0;
      return count || 0;
    },
  });

  const isLimitReached = !isPro && maxLoans !== null && loansCount >= maxLoans;

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen && isLimitReached) {
      setShowProModal(true);
      return;
    }
    setOpen(newOpen);
  };

  const form = useForm<z.infer<typeof loanSchema>>({
    resolver: zodResolver(loanSchema),
    defaultValues: {
      clientId: "",
      amount: "",
      interestRate: "40",
      interestType: "monthly",
      installments: "1",
      dailyDays: "20",
      dailyPenalty: "10",
      installmentFrequency: "monthly",
      loanDate: getLocalDateInputValue(),
      dueDate: "",
    },
  });

  // Parcelas personalizadas (apenas Valor Fixo)
  const [customEnabled, setCustomEnabled] = useState(false);
  const [customValues, setCustomValues] = useState<string[]>([]);
  const watchedInstallments = form.watch("installments");
  const watchedAmount = form.watch("amount");
  const watchedInterestType = form.watch("interestType");

  useEffect(() => {
    if (!customEnabled || watchedInterestType !== "fixed") return;
    const count = Math.max(1, parseInt(watchedInstallments || "1") || 1);
    setCustomValues((prev) => {
      if (prev.length === count) return prev;
      const base = round2((parseFloat(watchedAmount || "0") || 0) / count);
      return Array.from({ length: count }, (_, i) => prev[i] ?? (base > 0 ? String(base) : ""));
    });
  }, [customEnabled, watchedInstallments, watchedAmount, watchedInterestType]);



  const { data: clients = [] } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Não autenticado");

      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .eq("user_id", user.id)
        .order("name");

      if (error) throw error;
      return data;
    },
  });

  const selectedClient = clients.find((c) => c.id === form.watch("clientId"));

  // Empréstimos do cliente selecionado para detectar duplicados
  const { data: clientLoans = [] } = useQuery({
    queryKey: ["client-loans-for-alert", selectedClient?.id],
    enabled: !!selectedClient?.id,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !selectedClient) return [];
      const { data, error } = await supabase
        .from("loans")
        .select("id, client_id, amount, due_date, loan_date, status, interest_type")
        .eq("user_id", user.id)
        .eq("client_id", selectedClient.id);
      if (error) return [];
      return data;
    },
  });

  const { data: clientPayments = [] } = useQuery({
    queryKey: ["client-payments-for-score", selectedClient?.id],
    enabled: !!selectedClient?.id,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !selectedClient) return [];
      const loanIds = (clientLoans as any[]).map((l) => l.id);
      if (loanIds.length === 0) return [];
      const { data, error } = await supabase
        .from("loan_payments")
        .select("loan_id, paid_at, interest_amount")
        .eq("user_id", user.id)
        .in("loan_id", loanIds);
      if (error) return [];
      return data;
    },
  });

  const parseLocal = (s: string) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const activeClientLoans = (clientLoans as any[]).filter(
    (l) => l.status === "active" || l.status === "overdue"
  );
  const totalOpen = activeClientLoans.reduce((s, l) => s + Number(l.amount), 0);
  const today = startOfDay(new Date());
  const maxOverdueDays = activeClientLoans.reduce((max, l) => {
    const due = parseLocal(l.due_date);
    if (due < today) {
      const d = differenceInCalendarDays(today, due);
      return d > max ? d : max;
    }
    return max;
  }, 0);

  const scoreInfo = selectedClient
    ? calculateClientScore(
        selectedClient.id,
        (clientLoans as any[]).map((l) => ({
          id: l.id,
          clientId: l.client_id,
          amount: Number(l.amount),
          dueDate: parseLocal(l.due_date),
          loanDate: parseLocal(l.loan_date),
          status: l.status,
          interestType: l.interest_type,
        })),
        (clientPayments as any[]).map((p) => ({
          loanId: p.loan_id,
          paidAt: new Date(p.paid_at),
          interestAmount: Number(p.interest_amount),
        }))
      )
    : null;

  const scoreToneClass =
    scoreInfo?.tone === "success"
      ? "bg-success/10 text-success border-success/20"
      : scoreInfo?.tone === "warning"
      ? "bg-gold/10 text-gold border-gold/20"
      : "bg-destructive/10 text-destructive border-destructive/20";

  const handleRenameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClient || !newClientName.trim()) return;

    setRenamingClient(true);
    try {
      const { error } = await supabase
        .from("clients")
        .update({ name: newClientName.trim() })
        .eq("id", selectedClient.id);

      if (error) throw error;

      toast.success("Nome do cliente atualizado!");
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setRenamingClient(false);
    }
  };

  const handleSubmit = async (values: z.infer<typeof loanSchema>) => {
    try {
      // Validação extra de segurança (anti-falha silenciosa)
      if (!values.clientId) {
        toast.error("Selecione um cliente");
        return;
      }
      if (!values.amount || isNaN(parseFloat(values.amount)) || parseFloat(values.amount) <= 0) {
        toast.error("Informe um valor válido para o empréstimo");
        return;
      }
      if (values.interestType === "daily") {
        const days = parseInt(values.dailyDays || "");
        if (isNaN(days) || days < 1) {
          toast.error("Erro: Preencha a quantidade de dias");
          return;
        }
        const pen = parseFloat(values.dailyPenalty || "");
        if (isNaN(pen) || pen < 0) {
          toast.error("Erro: Preencha o valor da multa diária");
          return;
        }
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const insertPayload: any = {
        user_id: user.id,
        client_id: values.clientId,
        amount: parseFloat(values.amount),
        interest_rate: values.interestRate ? parseFloat(values.interestRate) : 0,
        interest_type: values.interestType,
        installments:
          values.interestType === "daily"
            ? parseInt(values.dailyDays || "1")
            : values.installments
            ? parseInt(values.installments)
            : 1,
        loan_date: values.loanDate,
        due_date: values.dueDate,
        status: "active",
      };

      if (values.interestType === "daily" || values.interestType === "monthly") {
        insertPayload.daily_penalty = parseFloat(values.dailyPenalty || "0");
      }

      if (values.interestType === "fixed") {
        insertPayload.installment_frequency = values.installmentFrequency || "monthly";
        insertPayload.installment_values = null;

        if (customEnabled) {
          const count = insertPayload.installments as number;
          if (customValues.length !== count) {
            toast.error("Informe o valor de todas as parcelas");
            return;
          }
          const parsed: number[] = [];
          for (const raw of customValues) {
            if (raw === undefined || String(raw).trim() === "") {
              toast.error("Nenhuma parcela pode ficar vazia");
              return;
            }
            const n = parseFloat(String(raw));
            if (!Number.isFinite(n) || n <= 0) {
              toast.error("Os valores das parcelas devem ser maiores que zero");
              return;
            }
            parsed.push(round2(n));
          }
          insertPayload.installment_values = parsed;
          insertPayload.amount = sumInstallments(parsed);
        }
      }

      const { error } = await supabase.from("loans").insert(insertPayload);

      if (error) {
        console.error("Erro ao inserir empréstimo:", error);
        throw error;
      }

      toast.success("Empréstimo cadastrado com sucesso!");
      setOpen(false);
      form.reset({
        clientId: "",
        amount: "",
        interestRate: "40",
        interestType: "monthly",
        installments: "1",
        dailyDays: "20",
        dailyPenalty: "10",
        installmentFrequency: "monthly",
        loanDate: getLocalDateInputValue(),
        dueDate: "",
      });
      setCustomEnabled(false);
      setCustomValues([]);
      // Invalidate queries to refresh loan list and count
      queryClient.invalidateQueries({ queryKey: ["loans"] });
      queryClient.invalidateQueries({ queryKey: ["loans-count"] });
      onSuccess();
    } catch (error: any) {
      console.error("Falha ao cadastrar empréstimo:", error);
      toast.error(error?.message || "Erro ao cadastrar empréstimo");
    }
  };

  const handleInvalid = (errors: any) => {
    console.warn("Validação falhou:", errors);
    const firstError =
      errors?.clientId?.message ||
      errors?.amount?.message ||
      errors?.interestType?.message ||
      errors?.interestRate?.message ||
      errors?.installments?.message ||
      errors?.dailyDays?.message ||
      errors?.dailyPenalty?.message ||
      errors?.loanDate?.message ||
      errors?.dueDate?.message ||
      "Verifique os campos do formulário";
    toast.error(firstError);
  };

  const handleRenameDialogOpen = () => {
    if (!selectedClient) {
      toast.error("Selecione um cliente primeiro");
      return;
    }
    setNewClientName(selectedClient.name);
    setOpen(false);
    setTimeout(() => setRenameDialogOpen(true), 100);
  };

  const handleRenameClose = () => {
    setRenameDialogOpen(false);
    setTimeout(() => setOpen(true), 100);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="premium" className="gap-2">
              <Plus className="h-4 w-4" />
              Adicionar cliente
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Empréstimo</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit, handleInvalid)} className="space-y-4" autoComplete="off">
              <FormField
                control={form.control}
                name="clientId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cliente</FormLabel>
                    <div className="flex gap-2">
                      <Select onValueChange={field.onChange} value={field.value} key="client-select">
                        <FormControl>
                          <SelectTrigger className="notranslate" translate="no">
                            <SelectValue placeholder="Selecione um cliente" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent position="popper" className="z-[200] notranslate" translate="no">
                          {clients.length > 0 ? (
                            clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                <span className="notranslate" translate="no">{client.name}</span>
                              </SelectItem>
                            ))
                          ) : (
                            <div className="py-2 px-3 text-sm text-muted-foreground notranslate" translate="no">
                              Nenhum cliente cadastrado
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleRenameDialogOpen}
                        disabled={!field.value}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {selectedClient && scoreInfo && (
                <div className={`flex items-center justify-between rounded-xl border px-3 py-2 text-xs ${scoreToneClass}`}>
                  <span className="font-semibold">
                    Score: {scoreInfo.score}/100
                  </span>
                  <span className="opacity-90">{scoreInfo.label}</span>
                </div>
              )}

              {selectedClient && activeClientLoans.length > 0 && (
                <Alert variant="destructive" className="border-destructive/40 bg-destructive/10">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Atenção: este cliente já possui empréstimo ativo.</AlertTitle>
                  <AlertDescription className="space-y-1 mt-2 text-xs">
                    <div><strong>Cliente:</strong> {selectedClient.name}</div>
                    <div><strong>Valor em aberto:</strong> {fmtMoney(totalOpen)}</div>
                    <div><strong>Empréstimos ativos:</strong> {activeClientLoans.length}</div>
                    {maxOverdueDays > 0 && (
                      <div><strong>Atraso máximo:</strong> {maxOverdueDays} dia{maxOverdueDays === 1 ? "" : "s"}</div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valor ({fmtCountry.currencySymbol})</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" placeholder="5000.00" autoComplete="off" inputMode="decimal" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="interestRate"
                render={({ field }) => {
                  const type = form.watch("interestType");
                  const isHidden = type === "fixed";
                  const label =
                    type === "daily" ? "Taxa de Juros Total (%)" : "Taxa de Juros Mensal (%)";
                  return (
                    <FormItem className={isHidden ? "hidden" : ""}>
                      <FormLabel>{label}</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.1" placeholder="40.0" autoComplete="off" inputMode="decimal" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="interestType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Juros</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} key="interest-type-select">
                      <FormControl>
                        <SelectTrigger className="notranslate" translate="no">
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent position="popper" className="z-[200] notranslate" translate="no">
                        <SelectItem value="monthly"><span className="notranslate" translate="no">Juros Mensal</span></SelectItem>
                        <SelectItem value="fixed"><span className="notranslate" translate="no">Juros Fixo</span></SelectItem>
                        <SelectItem value="daily"><span className="notranslate" translate="no">Diário</span></SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="installments"
                render={({ field }) => {
                  const isHidden = form.watch("interestType") !== "fixed";
                  return (
                    <FormItem className={isHidden ? "hidden" : ""}>
                      <FormLabel>Número de Parcelas</FormLabel>
                      <FormControl>
                        <Input type="number" min="1" placeholder="12" autoComplete="off" inputMode="numeric" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="installmentFrequency"
                render={({ field }) => {
                  const isHidden = form.watch("interestType") !== "fixed";
                  return (
                    <FormItem className={isHidden ? "hidden" : ""}>
                      <FormLabel>Periodicidade das Parcelas</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || "monthly"} key="installment-frequency-select">
                        <FormControl>
                          <SelectTrigger className="notranslate" translate="no">
                            <SelectValue placeholder="Selecione a periodicidade" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent position="popper" className="z-[200] notranslate" translate="no">
                          <SelectItem value="weekly"><span className="notranslate" translate="no">Semanal (a cada 7 dias)</span></SelectItem>
                          <SelectItem value="biweekly"><span className="notranslate" translate="no">Quinzenal (a cada 15 dias)</span></SelectItem>
                          <SelectItem value="monthly"><span className="notranslate" translate="no">Mensal (a cada 1 mês)</span></SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">A data de vencimento informada é a 1ª parcela. As próximas seguem essa periodicidade.</p>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              {form.watch("interestType") === "fixed" && (
                <div className="space-y-3 rounded-xl border border-border/60 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label className="text-sm">Personalizar valores das parcelas</Label>
                      <p className="text-xs text-muted-foreground">
                        Defina um valor diferente para cada parcela.
                      </p>
                    </div>
                    <Switch checked={customEnabled} onCheckedChange={setCustomEnabled} />
                  </div>

                  {customEnabled && (
                    <div className="space-y-2">
                      {customValues.map((v, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="w-20 shrink-0 text-xs text-muted-foreground notranslate" translate="no">
                            Parcela {i + 1}
                          </span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            inputMode="decimal"
                            value={v}
                            onChange={(e) => {
                              const next = [...customValues];
                              next[i] = e.target.value;
                              setCustomValues(next);
                            }}
                          />
                        </div>
                      ))}
                      <div className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-sm">
                        <span className="text-muted-foreground">Total do empréstimo</span>
                        <span className="font-bold notranslate" translate="no">
                          {fmtMoney(sumInstallments(customValues))}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}


              <FormField
                control={form.control}
                name="dailyDays"
                render={({ field }) => {
                  const isHidden = form.watch("interestType") !== "daily";
                  return (
                    <FormItem className={isHidden ? "hidden" : ""}>
                      <FormLabel>Quantidade de Dias para Pagamento</FormLabel>
                      <FormControl>
                        <Input type="number" min="1" placeholder="20" autoComplete="off" inputMode="numeric" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="dailyPenalty"
                render={({ field }) => {
                  const type = form.watch("interestType");
                  const isHidden = type !== "daily" && type !== "monthly";
                  return (
                    <FormItem className={isHidden ? "hidden" : ""}>
                      <FormLabel>Multa por dia de atraso ({fmtCountry.currencySymbol})</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" min="0" placeholder="10.00" autoComplete="off" inputMode="decimal" {...field} />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">Cobrada para cada dia após o vencimento.</p>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="loanDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data do Empréstimo</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dueDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data de Vencimento</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InterestCalculator 
                amount={form.watch("amount")} 
                interestRate={form.watch("interestRate")} 
              />

              <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Cadastrando..." : "Cadastrar Empréstimo"}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={renameDialogOpen} onOpenChange={(isOpen) => {
        if (!isOpen) handleRenameClose();
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Renomear Cliente</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            await handleRenameSubmit(e);
            handleRenameClose();
          }} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newName">Novo Nome</Label>
              <Input
                id="newName"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                placeholder="Digite o novo nome"
                required
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleRenameClose}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={renamingClient} className="flex-1">
                {renamingClient ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ProUpgradeModal 
        open={showProModal} 
        onOpenChange={setShowProModal} 
        limitType="loans" 
      />
    </>
  );
}
